import type { N8NServiceSchema } from "@/types/entities/n8n";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
  Switch,
} from "@elaniintc/goes-design-system";
import { Renderify } from "renderify-js";
import { DateInput } from "../../form/components/DateInput";
import TimeInput from "../../form/components/TimeInput";
import { VariableSelector } from "../../if/VariableSelector";
import type { NodeVariable } from "../../types";
import { useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import { useMemo } from "react";

interface Props {
  data: N8NServiceSchema["sections"][number]["inputs"][string];
  variableName: string;
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}

const parseN8NTypeToMetaType = (type: string) => {
  switch (type) {
    case "dui_nit":
      return "id";
    case "datetime":
      return "date-time";
    case "date_range":
      return "date-range";
    case "datetime_range":
      return "date-time-range";
    default:
      return type;
  }
};

const getTypeConfig = (type: string) => {
  switch (type) {
    case "text":
      return {
        icon: "text",
        placeholder: "Escribe un texto",
      };
    case "number":
      return {
        icon: "hashtag",
        placeholder: "Escribe un número",
      };
    case "id":
      return {
        icon: "id-card",
        placeholder: "Escribe un ID",
      };
    case "boolean":
      return {
        icon: "toggle-on",
        placeholder: "true / false",
      };
    case "email":
      return {
        icon: "envelope",
        placeholder: "Escribe un correo electrónico",
      };
    case "date":
    case "date-time":
    case "date-range":
    case "date-time-range":
      return {
        icon: "",
        placeholder: "DD/MM/YYYY",
      };
    default:
      return {
        icon: "question-circle",
        placeholder: "Ingresa un valor",
      };
  }
};

const getSystemType = (type: string) => {
  switch (type) {
    case "text":
    case "email":
      return "string";
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "date":
    case "date-time":
    case "date-range":
    case "date-time-range":
      return "date";
    case "array":
      return "array";
    default:
      return type;
  }
};

const InputType = ({
  data,
  variableName,
  nodeVariables,
  isLoadingVariables,
}: Props) => {
  const config = getTypeConfig(data.type);
  const { watch, setValue, formState, clearErrors } = useFormContext();

  const fieldValue = watch(variableName);

  const inputType = fieldValue?.type || "input";
  const error =
    (
      formState.errors[variableName] as {
        value?: { message: string };
      }
    )?.value?.message || "";

  const selectedVariable = useMemo(() => {
    if (inputType === "variable" && fieldValue?.value && fieldValue?.node_id) {
      const nodeVar = nodeVariables.find(
        (nv) => nv.nodeId === fieldValue.node_id,
      );

      if (!nodeVar) return undefined;

      let variable = nodeVar.variables.find((v) => v.id === fieldValue.value);

      if (!variable && fieldValue.field_id && fieldValue.output_name) {
        const formFormatId = `${fieldValue.field_id}-${fieldValue.output_name}`;
        variable = nodeVar.variables.find((v) => v.id === formFormatId);

        if (!variable) {
          variable = nodeVar.variables.find(
            (v) =>
              v.field_id === fieldValue.field_id &&
              v.output_name === fieldValue.output_name,
          );
        }
      }

      if (!variable && fieldValue.output_name) {
        variable = nodeVar.variables.find(
          (v) => v.output_name === fieldValue.output_name,
        );
      }

      if (!variable && fieldValue.value.includes("-")) {
        const parts = fieldValue.value.split("-");
        if (parts.length >= 2) {
          const possibleFieldId = parts[0];
          const possibleOutputName = parts.slice(1).join("-");
          variable = nodeVar.variables.find(
            (v) =>
              v.field_id === possibleFieldId &&
              v.output_name === possibleOutputName,
          );
        }
      }

      if (!variable && fieldValue.value === fieldValue.node_id) {
        return nodeVar.variables[0];
      }

      return variable;
    }
    return undefined;
  }, [inputType, fieldValue, nodeVariables]);

  if (data.type === "date-time") {
    return (
      <DateTimeInputWrapper
        variableName={variableName}
        data={data}
        nodeVariables={nodeVariables}
        isLoadingVariables={isLoadingVariables}
      />
    );
  }

  if (data.type === "date-time-range") {
    return (
      <DateTimeRange
        data={data}
        variableName={variableName}
        nodeVariables={nodeVariables}
        isLoadingVariables={isLoadingVariables}
      />
    );
  }

  if (data.type === "date-range") {
    return (
      <DateRangeInput
        data={data}
        variableName={variableName}
        nodeVariables={nodeVariables}
        isLoadingVariables={isLoadingVariables}
      />
    );
  }

  if (data.type === "array" || data.type === "select") {
    return (
      <ArrayInput
        data={data}
        nodeVariables={nodeVariables}
        variableName={variableName}
        isLoadingVariables={isLoadingVariables}
      />
    );
  }

  if (data.type === "file") {
    return (
      <FileInput
        data={data}
        variableName={variableName}
        nodeVariables={nodeVariables}
        isLoadingVariables={isLoadingVariables}
      />
    );
  }

  return (
    <GroupTextField
      label={variableName}
      className="flex-1 gap-2"
      required={data.required}
      error={error}
    >
      <GroupTextFieldRow className="overflow-x-hidden">
        <Button
          variant="quiet"
          group="up-left"
          onClick={() => {
            setValue(variableName, {
              type: inputType === "input" ? "variable" : "input",
              value: "",
              node_id: "",
            });
          }}
        >
          {inputType === "input" ? "inp" : "var"}
        </Button>
        {inputType === "input" ? (
          <>
            <Renderify condition={data.type === "boolean"}>
              <BooleanInput
                value={fieldValue?.value === "true" ? true : false}
                onChange={(value) => {
                  setValue(variableName, {
                    ...fieldValue,
                    value: value.toString(),
                    node_id: "",
                  });
                }}
              />
            </Renderify>
            <Renderify condition={data.type !== "boolean"}>
              <div className="relative w-full">
                {config.icon && (
                  <Icon
                    name={config.icon}
                    type="far"
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-body"
                  />
                )}
                <Renderify condition={data.type === "number"}>
                  <NumberInput
                    config={config}
                    onChange={(val) => {
                      setValue(variableName, {
                        type: fieldValue.type,
                        value: val,
                      });
                    }}
                    value={fieldValue?.value}
                  />
                </Renderify>
                <Renderify condition={data.type === "text"}>
                  <TextInput
                    config={config}
                    onChange={(val) => {
                      setValue(variableName, {
                        type: fieldValue.type,
                        value: val,
                      });
                    }}
                    value={fieldValue?.value}
                  />
                </Renderify>
                <Renderify condition={data.type === "email"}>
                  <EmailInput
                    config={config}
                    onChange={(value) => {
                      setValue(variableName, {
                        type: fieldValue.type,
                        value: value,
                      });
                    }}
                    value={fieldValue?.value}
                  />
                </Renderify>
                <Renderify condition={data.type === "id"}>
                  <IDInput
                    config={config}
                    onChange={(val) => {
                      setValue(variableName, {
                        type: fieldValue.type,
                        value: val,
                      });
                    }}
                    value={fieldValue?.value}
                  />
                </Renderify>
                <Renderify condition={data.type === "date"}>
                  <DateInput
                    onChangeDate={(val) => {
                      setValue(variableName, {
                        ...fieldValue,
                        value: dayjs(val).format("DD/MM/YYYY"),
                      });
                    }}
                    group="right"
                    placeholder="DD/MM/YYYY"
                    value={fieldValue?.value || ""}
                    className="border-l-0"
                  />
                </Renderify>
              </div>
            </Renderify>
          </>
        ) : (
          <VariableSelector
            variables={nodeVariables
              .map((v) => {
                return {
                  ...v,
                  variables: (v.variables ?? [])
                    .filter(({ type, meta_type }) => {
                      if (meta_type) {
                        return parseN8NTypeToMetaType(meta_type) === data.type;
                      }
                      const systemType = getSystemType(data.type);
                      return (
                        // filter uuid as string and validating with assertion in order to no break existing typings
                        type === systemType ||
                        (systemType === "string" && (type as string) === "uuid")
                      );
                    })
                    // map uuid to string type and validating with assertion in order to no break existing typings
                    .map((variable) => ({
                      ...variable,
                      type:
                        (variable.type as string) === "uuid"
                          ? "string"
                          : variable.type,
                    })),
                };
              })
              .filter(({ variables }) => {
                return variables.length > 0;
              })}
            onSelectVariable={(v) => {
              setValue(variableName, {
                ...fieldValue,
                value: v?.id,
                node_id: v?.node_id,
                field_id: v?.field_id,
                output_name: v?.output_name,
              });
              clearErrors(variableName);
            }}
            selectedVariable={{
              id: selectedVariable?.id || "",
              type: fieldValue?.type || "string",
              name: selectedVariable?.name || "",
              field_id: selectedVariable?.field_id || "",
              output_name: selectedVariable?.output_name || "",
              node_id: fieldValue?.node_id || "",
              ref_name: selectedVariable?.ref_name || "",
            }}
            isLoading={isLoadingVariables}
            triggerGroup="right"
            placeholder="Elige una variable..."
          />
        )}
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

export default InputType;

const BooleanInput = ({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (value: boolean) => void;
}) => {
  return (
    <div className="w-full bg-neutral-object-accent-silent border border-l-0 border-neutral rounded-r-lg flex items-center justify-start px-3 py-2 gap-3">
      <Icon
        name="toggle-large-on"
        type="far"
        className="pointer-events-none text-body"
      />
      <Switch
        checked={value}
        onCheckedChange={onChange}
        aria-label="Toggle boolean value"
      />
      <span className="text-body leading-3xl">{value ? "True" : "False"}</span>
    </div>
  );
};

const NumberInput = ({
  config,
  onChange,
  value,
}: {
  value: string;
  onChange: (value: string) => void;
  config: { placeholder: string };
}) => {
  return (
    <Input
      className={`w-full pl-9`}
      placeholder={config.placeholder}
      type="string"
      group="right"
      value={value}
      onChange={(e) => {
        const newValue = e.target.value;
        onChange(newValue);
      }}
    />
  );
};

const TextInput = ({
  config,
  onChange,
  value,
}: {
  value: string;
  onChange: (value: string) => void;
  config: { placeholder: string };
}) => {
  return (
    <Input
      className={`w-full pl-9`}
      placeholder={config.placeholder}
      //type={getInputType(type)}
      group="right"
      value={value}
      onChange={(e) => {
        const newValue = e.target.value;
        onChange(newValue);
      }}
    />
  );
};

const EmailInput = ({
  config,
  onChange,
  value,
}: {
  value: string;
  onChange: (value: string) => void;
  config: { placeholder: string };
}) => {
  return (
    <Input
      className={`w-full pl-9`}
      placeholder={config.placeholder}
      //type={getInputType(type)}
      group="right"
      value={value}
      onChange={(e) => {
        const newValue = e.target.value;
        onChange(newValue);
      }}
    />
  );
};

const IDInput = ({
  config,
  onChange,
  value,
}: {
  value: string;
  onChange: (value: string) => void;
  config: { placeholder: string };
}) => {
  return (
    <Input
      className={`w-full pl-9`}
      placeholder={config.placeholder}
      //type={getInputType(type)}
      group="right"
      value={value}
      onChange={(e) => {
        const newValue = e.target.value;
        onChange(newValue);
      }}
    />
  );
};

const DateTimeInputWrapper = ({
  variableName,
  data,
  nodeVariables,
  isLoadingVariables,
}: {
  variableName: string;
  data: N8NServiceSchema["sections"][number]["inputs"][string];
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}) => {
  const { watch, setValue, formState, clearErrors } = useFormContext();
  const field = watch(variableName);
  const inputType = field?.type || "input";

  const error = formState.errors[variableName] as {
    date?: { message: string };
    time?: { message: string };
  };

  const composeError = useMemo(() => {
    let errorMessage = "";
    if (error?.date?.message) {
      errorMessage += error.date.message;
    }
    if (error?.time?.message) {
      if (errorMessage) {
        errorMessage += " ";
      }
      errorMessage += error.time.message;
    }
    return errorMessage;
  }, [error]);

  return (
    <GroupTextField
      label={variableName}
      required={data.required}
      className="gap-2"
      error={composeError}
    >
      <GroupTextFieldRow className="overflow-x-hidden">
        <Button
          className="h-full"
          variant="quiet"
          group="left"
          onClick={() => {
            setValue(variableName, {
              ...field,
              type: inputType === "input" ? "variable" : "input",
            });
          }}
        >
          {inputType === "input" ? "inp" : "var"}
        </Button>

        {inputType === "input" ? (
          <DateTimeInput
            timeGroup="right"
            variableName={variableName}
            dateName="date"
            timeName="time"
          />
        ) : (
          <VariableSelector
            variables={nodeVariables
              .map((v) => {
                return {
                  ...v,
                  variables: (v.variables ?? []).filter(
                    ({ type, meta_type }) => {
                      if (meta_type) {
                        return parseN8NTypeToMetaType(meta_type) === data.type;
                      }
                      return type === getSystemType(data.type);
                    },
                  ),
                };
              })
              .filter(({ variables }) => {
                return variables.length > 0;
              })}
            onSelectVariable={(val) => {
              setValue(variableName, {
                ...field,
                value: val?.id,
                node_start_id: val?.node_id,
                value_type: "field",
              });
              clearErrors(variableName);
            }}
            selectedVariable={null}
            triggerGroup="right"
            placeholder="Elige una variable..."
            isLoading={isLoadingVariables}
          />
        )}
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

const DateTimeInput = ({
  variableName,
  timeGroup = "right",
  dateName,
  timeName,
}: {
  variableName: string;
  timeGroup?: "right" | "down-right" | "up-right";
  timeName: string;
  dateName: string;
}) => {
  const { watch, setValue } = useFormContext();
  const field = watch(variableName);
  return (
    <div className="w-full relative flex">
      <DateInput
        onChangeDate={(val) => {
          setValue(variableName, {
            ...field,
            [dateName]: dayjs(val).format("DD/MM/YYYY"),
          });
        }}
        group="middle"
        placeholder="DD/MM/YYYY"
        value={field[dateName] || ""}
        className="border-l-0"
        wrapperClassName="w-1/2"
      />
      <TimeInput
        onChange={(val) => {
          setValue(variableName, {
            ...field,
            [timeName]: val,
          });
        }}
        group={timeGroup}
        placeholder="HH:MM AM/PM"
        value={field[timeName] || ""}
        shouldUse24={false}
        wrapperClassName="w-1/2"
        className="border-l-0"
      />
    </div>
  );
};

const DateTimeRange = ({
  variableName,
  data,
  nodeVariables,
  isLoadingVariables,
}: {
  variableName: string;
  data: N8NServiceSchema["sections"][number]["inputs"][string];
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}) => {
  const { watch, setValue, formState, clearErrors } = useFormContext();
  const field = watch(variableName);
  const type = field.type || "input";
  const error = formState.errors[variableName] as {
    start?: { message: string };
    end?: { message: string };
    start_time?: { message: string };
    end_time?: { message: string };
  };
  // create string only of any error present
  const composeError = useMemo(() => {
    let errorMessage = "";
    if (error?.start?.message) {
      errorMessage += error.start.message;
    }
    if (error?.end_time?.message) {
      errorMessage += " " + error.end_time.message;
    }
    if (error?.end?.message) {
      if (errorMessage) {
        errorMessage += " ";
      }
      errorMessage += error.end.message;
    }
    if (error?.start_time?.message) {
      if (errorMessage) {
        errorMessage += " " + error.start_time.message;
      }
    }
    return errorMessage;
  }, [error]);

  const selectedVariable = useMemo(() => {
    if (type === "variable" && field?.value && field?.node_id) {
      return nodeVariables
        .find((nv) => nv.nodeId === field.node_id)
        ?.variables.find((v) => v.id === field.value);
    }
    return undefined;
  }, [type, field, nodeVariables]);

  if (type === "input") {
    return (
      <GroupTextField
        label={variableName}
        required={data.required}
        className="gap-2"
        error={composeError}
      >
        <GroupTextFieldRow className="overflow-x-hidden">
          <div className="h-full flex items-center">
            <span className="w-[6ch] text-body-quiet">Inicio</span>
          </div>
          <div>
            <Button
              className="h-full"
              variant="quiet"
              group="up-left"
              onClick={() => {
                setValue(variableName, {
                  ...field,
                  type: "variable",
                });
              }}
            >
              inp
            </Button>
          </div>
          <DateTimeInput
            timeGroup="up-right"
            variableName={variableName}
            dateName="start"
            timeName="start_time"
          />
        </GroupTextFieldRow>
        <GroupTextFieldRow className="overflow-x-hidden">
          <div className="h-full flex items-center">
            <span className="w-[6ch] text-body-quiet">Final</span>
          </div>
          <div>
            <Button
              className="h-full"
              variant="quiet"
              group="down-left"
              onClick={() => {
                setValue(variableName, {
                  ...field,
                  type: "variable",
                });
              }}
            >
              inp
            </Button>
          </div>
          <DateTimeInput
            timeGroup="down-right"
            dateName="end"
            timeName="end_time"
            variableName={variableName}
          />
        </GroupTextFieldRow>
      </GroupTextField>
    );
  }

  return (
    <GroupTextField
      label={variableName}
      required={data.required}
      className="gap-2"
      error={composeError}
    >
      <GroupTextFieldRow className="overflow-x-hidden">
        <div className="h-full flex items-center">
          <span className="w-[6ch] text-body-quiet">Rango</span>
        </div>
        <div>
          <Button
            className="h-full"
            variant="quiet"
            group="left"
            onClick={() => {
              setValue(variableName, {
                ...field,
                type: "input",
              });
            }}
          >
            var
          </Button>
        </div>
        <VariableSelector
          variables={nodeVariables
            .map((v) => {
              return {
                ...v,
                variables: (v.variables ?? []).filter(({ type, meta_type }) => {
                  if (meta_type) {
                    return parseN8NTypeToMetaType(meta_type) === data.type;
                  }
                  return type === getSystemType(data.type);
                }),
              };
            })
            .filter(({ variables }) => {
              return variables.length > 0;
            })}
          onSelectVariable={(val) => {
            setValue(variableName, {
              ...field,
              value: val?.id,
              node_id: val?.node_id,
            });
            clearErrors(variableName);
          }}
          selectedVariable={{
            id: field?.value || "",
            type: field?.type || "string",
            name: selectedVariable?.name || "",
            node_id: field?.node_id || "",
            ref_name: selectedVariable?.ref_name || "",
          }}
          triggerGroup="right"
          placeholder="Elige una variable..."
          isLoading={isLoadingVariables}
        />
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

{
  /*  */
}

const DateRangeInput = ({
  variableName,
  data,
  nodeVariables,
  isLoadingVariables,
}: {
  variableName: string;
  data: N8NServiceSchema["sections"][number]["inputs"][string];
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}) => {
  const { watch, setValue, formState, clearErrors } = useFormContext();
  const field = watch(variableName);
  const inputType = field?.type || "input";
  const error = formState.errors[variableName] as {
    start?: { message: string };
    end?: { message: string };
  };

  const composeError = useMemo(() => {
    let errorMessage = "";
    if (error?.start?.message) {
      errorMessage += error.start.message;
    }
    if (error?.end?.message) {
      if (errorMessage) {
        errorMessage += " ";
      }
      errorMessage += error.end.message;
    }
    return errorMessage;
  }, [error]);

  return (
    <GroupTextField
      label={variableName}
      required={data.required}
      className="gap-2"
      error={composeError}
    >
      <GroupTextFieldRow className="overflow-x-hidden">
        <Button
          className="h-full"
          variant="quiet"
          group="up-left"
          onClick={() => {
            setValue(variableName, {
              ...field,
              type: inputType === "input" ? "variable" : "input",
            });
          }}
        >
          {inputType === "input" ? "inp" : "var"}
        </Button>
        {inputType === "input" ? (
          <>
            <DateInput
              onChangeDate={(val) => {
                setValue(variableName, {
                  ...field,
                  start: dayjs(val).format("DD/MM/YYYY"),
                });
              }}
              group="middle"
              placeholder="DD/MM/YYYY"
              value={field.start || ""}
              className="border-l-0"
              wrapperClassName="w-1/2"
            />
            <DateInput
              onChangeDate={(val) => {
                setValue(variableName, {
                  ...field,
                  end: dayjs(val).format("DD/MM/YYYY"),
                });
              }}
              group="right"
              placeholder="DD/MM/YYYY"
              value={field.end || ""}
              className="border-l-0"
              wrapperClassName="w-1/2"
            />
          </>
        ) : (
          <VariableSelector
            variables={nodeVariables
              .map((v) => {
                return {
                  ...v,
                  variables: (v.variables ?? []).filter(
                    ({ type, meta_type }) => {
                      if (meta_type) {
                        return parseN8NTypeToMetaType(meta_type) === data.type;
                      }
                      return type === getSystemType(data.type);
                    },
                  ),
                };
              })
              .filter(({ variables }) => {
                return variables.length > 0;
              })}
            onSelectVariable={(val) => {
              setValue(variableName, {
                ...field,
                value: val?.id,
                node_id: val?.node_id,
              });
              clearErrors(variableName);
            }}
            selectedVariable={null}
            triggerGroup="right"
            placeholder="Elige una variable..."
            isLoading={isLoadingVariables}
          />
        )}
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

const ArrayInput = ({
  variableName,
  data,
  nodeVariables,
  isLoadingVariables,
}: {
  variableName: string;
  data: N8NServiceSchema["sections"][number]["inputs"][string];
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}) => {
  const { watch, setValue, formState, clearErrors } = useFormContext();
  const field = watch(variableName);
  const error = formState.errors[variableName] as {
    value?: { message: string };
  };

  // const selectedVariable = useMemo(() => {
  //   if (!field?.node_id) return null;
  //   return nodeVariables
  //     .map((v) => {
  //       return {
  //         ...v,
  //         variables: (v.variables ?? []).filter(({ type, meta_type }) => {
  //           return type === "array" || meta_type === "select";
  //         }),
  //       };
  //     })
  //     .flatMap((v) => v.variables || [])
  //     .find((variable) => variable.id === field.node_id);
  // }, [field, nodeVariables]);
  const selectedVariable = useMemo(() => {
    if (!field?.node_id) return null;
    return nodeVariables
      .map((v) => {
        return {
          ...v,
          variables: v.variables ?? [],
        };
      })
      .flatMap((v) => v.variables || [])
      .find((variable) => {
        return variable.id === field.value;
      });
  }, [field, nodeVariables]);

  return (
    <GroupTextField
      label={variableName}
      required={data.required}
      className="gap-2"
      error={error?.value?.message || ""}
    >
      <GroupTextFieldRow className="overflow-x-hidden">
        <VariableSelector
          variables={nodeVariables
            .map((v) => {
              return {
                ...v,
                variables: (v.variables ?? []).filter(({ type, meta_type }) => {
                  return type === "array" || meta_type === "select";
                }),
              };
            })
            .filter(({ variables }) => {
              return variables.length > 0;
            })}
          onSelectVariable={(val) => {
            console.log(variableName, {
              ...field,
              value: val?.id,
              node_id: val?.node_id,
              field_id: val?.field_id,
              output_name: val?.output_name,
            });
            setValue(variableName, {
              ...field,
              value: val?.id,
              node_id: val?.node_id,
              field_id: val?.field_id,
              output_name: val?.output_name,
            });
            clearErrors(variableName);
          }}
          selectedVariable={{
            id: selectedVariable?.id || "",
            node_id: selectedVariable?.node_id || "",
            name: selectedVariable?.name || "",
            type: selectedVariable?.type || "array",
            ref_name: selectedVariable?.ref_name || "",
          }}
          placeholder="Seleccionar variable"
          isLoading={isLoadingVariables}
        />
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

const FileInput = ({
  variableName,
  data,
  nodeVariables,
  isLoadingVariables,
}: {
  variableName: string;
  data: N8NServiceSchema["sections"][number]["inputs"][string];
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}) => {
  const { watch, setValue, formState, clearErrors } = useFormContext();
  const field = watch(variableName);
  const error = formState.errors[variableName] as {
    value?: { message: string };
  };
  const selectedVariable = useMemo(() => {
    if (!field?.node_id) return null;
    return nodeVariables
      .map((v) => {
        return {
          ...v,
          variables: (v.variables ?? []).filter(({ type }) => {
            return type === "file";
          }),
        };
      })
      .flatMap((v) => v.variables || [])
      .find((variable) => variable.id === field.value);
  }, [field, nodeVariables]);
  console.log(selectedVariable);
  return (
    <div className="flex gap-2 items-center">
      <GroupTextField
        label={variableName}
        required={data.required}
        className="gap-2 w-full"
        error={error?.value?.message || ""}
      >
        <GroupTextFieldRow className="overflow-x-hidden">
          <VariableSelector
            variables={nodeVariables
              .map((v) => {
                return {
                  ...v,
                  variables: (v.variables ?? []).filter(
                    ({ type, meta_type }) => {
                      if (meta_type) {
                        return parseN8NTypeToMetaType(meta_type) === data.type;
                      }
                      return type === getSystemType(data.type);
                    },
                  ),
                };
              })
              .filter(({ variables }) => {
                return variables.length > 0;
              })}
            onSelectVariable={(val) => {
              setValue(variableName, {
                ...field,
                value: val?.id,
                node_id: val?.node_id,
              });
              clearErrors(variableName);
            }}
            selectedVariable={{
              id: selectedVariable?.id || "",
              node_id: selectedVariable?.node_id || "",
              name: selectedVariable?.name || "",
              type: selectedVariable?.type || "file",
              ref_name: selectedVariable?.ref_name || "",
            }}
            placeholder="Elige una variable..."
            isLoading={isLoadingVariables}
          />
        </GroupTextFieldRow>
      </GroupTextField>
      {/* {field.value && (
        <Button
          variant="tertiary"
          className="size-11 mt-5"
          onClick={() => {
            setValue(variableName, {
              ...field,
              value: "",
              node_id: "",
            });
          }}
        >
          <Icon name="close" type="far" />
        </Button>
      )} */}
    </div>
  );
};

// const SelectInput = ({
//   variableName,
//   data,
//   nodeVariables,
//   isLoadingVariables,
// }: {
//   variableName: string;
//   data: N8NServiceSchema["sections"][number]["inputs"][string];
//   nodeVariables: NodeVariable[];
//   isLoadingVariables?: boolean;
// }) => {
//   const { watch, setValue, formState, clearErrors } = useFormContext();
//   const field = watch(variableName);
//   console.log(field);
//   const error = formState.errors[variableName] as {
//     value?: { message: string };
//   };
//   const selectedVariable = useMemo(() => {
//     if (!field?.node_id) return null;
//     return nodeVariables
//       .map((v) => {
//         return {
//           ...v,
//           variables: v.variables ?? [],
//         };
//       })
//       .flatMap((v) => v.variables || [])
//       .find((variable) => variable.id === field.value);
//   }, [field, nodeVariables]);

//   return (
//     <div className="flex gap-2 items-center">
//       <GroupTextField
//         label={variableName}
//         required={data.required}
//         className="gap-2 w-full"
//         error={error?.value?.message || ""}
//       >
//         <GroupTextFieldRow className="overflow-x-hidden">
//           <VariableSelector
//             variables={nodeVariables
//               .map((v) => {
//                 return {
//                   ...v,
//                   variables: (v.variables ?? []).filter(
//                     ({ type, meta_type }) => {
//                       if (meta_type) {
//                         return parseN8NTypeToMetaType(meta_type) === data.type;
//                       }
//                       return type === getSystemType(data.type);
//                     },
//                   ),
//                 };
//               })
//               .filter(({ variables }) => {
//                 return variables.length > 0;
//               })}
//             onSelectVariable={(val) => {
//               console.log(val, {
//                 ...field,
//                 value: val?.id,
//                 node_id: val?.node_id,
//               });
//               setValue(variableName, {
//                 ...field,
//                 value: val?.id,
//                 node_id: val?.node_id,
//               });
//               clearErrors(variableName);
//             }}
//             selectedVariable={{
//               id: selectedVariable?.id || "",
//               node_id: selectedVariable?.node_id || "",
//               name: selectedVariable?.name || "",
//               type: selectedVariable?.type || "file",
//               ref_name: selectedVariable?.ref_name || "",
//             }}
//             placeholder="Elige una variable..."
//             isLoading={isLoadingVariables}
//           />
//         </GroupTextFieldRow>
//       </GroupTextField>
//       {/* {field.value && (
//         <Button
//           variant="tertiary"
//           className="size-11 mt-5"
//           onClick={() => {
//             setValue(variableName, {
//               ...field,
//               value: "",
//               node_id: "",
//             });
//           }}
//         >
//           <Icon name="close" type="far" />
//         </Button>
//       )} */}
//     </div>
//   );
// };
